export const TARGETING_MODES = {
  ALL: 'all',
  SEGMENT: 'segment',
  CLIENTS: 'clients',
};

export const OFFER_TYPES = {
  POURCENTAGE: 'POURCENTAGE',
  MONTANT_FIXE: 'MONTANT_FIXE',
};

/** Canaux configurables Admin (enum Prisma CanalVente — sous-ensemble métier). */
export const PROMO_CHANNEL_OPTIONS = [
  { value: 'WEB', label: 'Web' },
  { value: 'CLICK_AND_COLLECT', label: 'Click & Collect' },
  { value: 'DELIVERY', label: 'Livraison' },
  { value: 'POS', label: 'POS / Caisse' },
];

export const OFFER_TYPE_OPTIONS = [
  { value: OFFER_TYPES.POURCENTAGE, label: 'Réduction en % sur le panier' },
  { value: OFFER_TYPES.MONTANT_FIXE, label: 'Montant fixe (€) sur le panier' },
];

export const SEGMENT_OPTIONS = [
  { value: 'TOUS', label: 'Tous les clients', emoji: '👥' },
  { value: 'NOUVEAUX', label: 'Nouveaux clients', emoji: '🆕' },
  { value: 'FIDELES', label: 'Clients fidèles', emoji: '⭐' },
  { value: 'VIP', label: 'Clients VIP', emoji: '👑' },
];

export function createEmptyPromotionForm(commercantId = '') {
  return {
    titre: '',
    description: '',
    commercantId,
    typeOffre: OFFER_TYPES.POURCENTAGE,
    valeur: '',
    targetingMode: TARGETING_MODES.ALL,
    segmentClient: 'TOUS',
    codePromo: '',
    montantMinimum: '',
    dateDebut: '',
    dateFin: '',
    limiteUtilisationParUser: '',
    listeClientsAutorises: [],
    selectedClients: [],
    canauxValides: [],
  };
}

export function resolveTargetingMode(promotion) {
  const list = promotion?.listeClientsAutorises || [];
  if (list.length > 0) return TARGETING_MODES.CLIENTS;
  if (promotion?.segmentClient && promotion.segmentClient !== 'TOUS') {
    return TARGETING_MODES.SEGMENT;
  }
  return TARGETING_MODES.ALL;
}

export function promotionToForm(promotion, fallbackCommercantId = '') {
  if (!promotion) return createEmptyPromotionForm(fallbackCommercantId);
  const targetingMode = resolveTargetingMode(promotion);
  return {
    titre: promotion.titre || '',
    description: promotion.description || '',
    commercantId: promotion.commercantId || fallbackCommercantId,
    typeOffre: promotion.typeReduction || OFFER_TYPES.POURCENTAGE,
    valeur: promotion.reduction != null ? String(promotion.reduction) : '',
    targetingMode,
    segmentClient: promotion.segmentClient || 'TOUS',
    codePromo: promotion.codePromo || '',
    montantMinimum:
      promotion.montantMinimum != null ? String(promotion.montantMinimum) : '',
    dateDebut: promotion.dateDebut
      ? new Date(promotion.dateDebut).toISOString().slice(0, 10)
      : '',
    dateFin: promotion.dateFin ? new Date(promotion.dateFin).toISOString().slice(0, 10) : '',
    limiteUtilisationParUser:
      promotion.limiteUtilisationParUser != null
        ? String(promotion.limiteUtilisationParUser)
        : '',
    listeClientsAutorises: promotion.listeClientsAutorises || [],
    selectedClients: (promotion.listeClientsAutorises || []).map((entry) => ({
      id: entry.includes('@') ? null : entry,
      email: entry.includes('@') ? entry : entry,
      label: entry,
    })),
    canauxValides: Array.isArray(promotion.canauxValides) ? [...promotion.canauxValides] : [],
  };
}

export function getPromotionReductionLabel(promotion) {
  const type = promotion?.typeReduction || OFFER_TYPES.POURCENTAGE;
  const value = promotion?.reduction;
  if (value == null) return '—';
  if (type === OFFER_TYPES.MONTANT_FIXE) {
    return `-${Number(value).toFixed(2).replace(/\.00$/, '')} €`;
  }
  return `-${value} %`;
}

export function getPromotionStatus(promotion) {
  if (!promotion?.actif) {
    return { key: 'disabled', label: 'Désactivée', className: 'bg-red-100 text-red-800' };
  }
  const now = new Date();
  const debut = new Date(promotion.dateDebut);
  const fin = new Date(promotion.dateFin);
  fin.setHours(23, 59, 59, 999);

  if (now < debut) {
    return { key: 'scheduled', label: 'Programmée', className: 'bg-blue-100 text-blue-800' };
  }
  if (now > fin) {
    return { key: 'expired', label: 'Expirée', className: 'bg-gray-100 text-gray-700' };
  }
  return { key: 'active', label: 'Active', className: 'bg-green-100 text-green-800' };
}

export function filterPromotionsByStatus(promotions, filter) {
  if (filter === 'all') return promotions;
  return promotions.filter((p) => getPromotionStatus(p).key === filter);
}

export function generatePromoCode(titre = 'PROMO') {
  const base = titre
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, 8);
  const suffix = Math.random().toString(36).slice(2, 6).toUpperCase();
  return `${base || 'PROMO'}${suffix}`;
}

export function normalizePromoCode(value) {
  return String(value || '')
    .trim()
    .toUpperCase()
    .replace(/\s+/g, '');
}

export function validatePromotionForm(form) {
  if (!form.titre?.trim()) {
    return 'Le titre est obligatoire.';
  }
  if (!form.commercantId) {
    return 'Sélectionnez un commerçant.';
  }
  const desc = form.description?.trim() || '';
  if (desc.length < 5) {
    return 'La description est obligatoire (5 caractères minimum).';
  }
  if (desc.length > 500) {
    return 'La description ne doit pas dépasser 500 caractères.';
  }

  const typeOffre = form.typeOffre || OFFER_TYPES.POURCENTAGE;
  const valeur = parseFloat(form.valeur);
  if (!form.valeur || Number.isNaN(valeur)) {
    return typeOffre === OFFER_TYPES.MONTANT_FIXE
      ? 'Indiquez un montant de réduction en euros.'
      : 'Indiquez un pourcentage de réduction.';
  }
  if (typeOffre === OFFER_TYPES.MONTANT_FIXE) {
    if (valeur < 0.01 || valeur > 10000) {
      return 'Le montant fixe doit être entre 0,01 € et 10 000 €.';
    }
  } else if (valeur < 1 || valeur > 100) {
    return 'Indiquez un pourcentage de réduction entre 1 et 100.';
  }

  if (!form.dateDebut || !form.dateFin) {
    return 'Les dates de début et de fin sont obligatoires.';
  }
  if (new Date(form.dateFin) < new Date(form.dateDebut)) {
    return 'La date de fin doit être postérieure à la date de début.';
  }
  const code = normalizePromoCode(form.codePromo);
  if (code.length < 2) {
    return 'Le code promo est obligatoire (2 caractères minimum).';
  }
  if (form.targetingMode === TARGETING_MODES.CLIENTS) {
    const list = buildAuthorizedClientList(form);
    if (list.length === 0) {
      return 'Sélectionnez au moins un client pour le ciblage « Clients choisis ».';
    }
  }
  if (form.limiteUtilisationParUser) {
    const lim = parseInt(form.limiteUtilisationParUser, 10);
    if (Number.isNaN(lim) || lim < 1) {
      return 'La limite par client doit être un entier ≥ 1, ou laissez vide pour illimité.';
    }
  }
  if (form.montantMinimum) {
    const min = parseFloat(form.montantMinimum);
    if (Number.isNaN(min) || min <= 0) {
      return 'Le montant minimum doit être supérieur à 0 €, ou laissez vide pour aucun minimum.';
    }
  }
  return null;
}

export function buildAuthorizedClientList(form) {
  if (form.targetingMode !== TARGETING_MODES.CLIENTS) return [];
  const fromSelected = (form.selectedClients || [])
    .map((c) => c.id || c.email)
    .filter(Boolean);
  const manual = (form.listeClientsAutorises || []).filter(Boolean);
  return [...new Set([...fromSelected, ...manual])];
}

export function buildPromotionPayload(form, { isEdit = false } = {}) {
  const targetingMode = form.targetingMode || TARGETING_MODES.ALL;
  let segmentClient = 'TOUS';
  let listeClientsAutorises = [];

  if (targetingMode === TARGETING_MODES.SEGMENT) {
    segmentClient = form.segmentClient || 'TOUS';
  } else if (targetingMode === TARGETING_MODES.CLIENTS) {
    segmentClient = 'TOUS';
    listeClientsAutorises = buildAuthorizedClientList(form);
  }

  const typeOffre = form.typeOffre || OFFER_TYPES.POURCENTAGE;

  return {
    titre: form.titre.trim(),
    description: form.description.trim(),
    commercantId: form.commercantId,
    typeReduction: typeOffre,
    reduction: parseFloat(form.valeur),
    codePromo: normalizePromoCode(form.codePromo),
    dateDebut: form.dateDebut,
    dateFin: form.dateFin,
    actif: isEdit ? form.actif !== false : true,
    segmentClient,
    limiteUtilisationParUser: form.limiteUtilisationParUser
      ? parseInt(form.limiteUtilisationParUser, 10)
      : null,
    montantMinimum: form.montantMinimum ? parseFloat(form.montantMinimum) : null,
    listeClientsAutorises,
    canauxValides: Array.isArray(form.canauxValides) ? form.canauxValides : [],
  };
}

export function buildPromotionPreview(form, commercantName = '') {
  const typeOffre = form.typeOffre || OFFER_TYPES.POURCENTAGE;
  const offre =
    typeOffre === OFFER_TYPES.MONTANT_FIXE
      ? form.valeur
        ? `${form.valeur} €`
        : '—'
      : form.valeur
        ? `${form.valeur} %`
        : '—';
  const code = normalizePromoCode(form.codePromo) || '—';
  let ciblage = 'Tous les clients';
  if (form.targetingMode === TARGETING_MODES.SEGMENT) {
    const seg = SEGMENT_OPTIONS.find((s) => s.value === form.segmentClient);
    ciblage = seg ? seg.label : form.segmentClient;
  } else if (form.targetingMode === TARGETING_MODES.CLIENTS) {
    const n = buildAuthorizedClientList(form).length;
    ciblage = n > 0 ? `${n} client(s) choisi(s)` : 'Clients choisis (à sélectionner)';
  }
  const dates =
    form.dateDebut && form.dateFin
      ? `du ${new Date(form.dateDebut + 'T12:00:00').toLocaleDateString('fr-FR')} au ${new Date(form.dateFin + 'T12:00:00').toLocaleDateString('fr-FR')}`
      : 'dates à définir';
  const limite = form.limiteUtilisationParUser
    ? `${form.limiteUtilisationParUser} utilisation(s) max. par client`
    : 'utilisations illimitées par client';
  const minimum = form.montantMinimum
    ? `Panier minimum : ${form.montantMinimum} € (hors frais de livraison)`
    : 'Aucun montant minimum de panier';
  const canaux = Array.isArray(form.canauxValides) ? form.canauxValides : [];
  const canauxLabel =
    canaux.length === 0
      ? 'Tous les canaux'
      : canaux
          .map((c) => PROMO_CHANNEL_OPTIONS.find((o) => o.value === c)?.label || c)
          .join(', ');

  return {
    headline: form.titre?.trim() || 'Votre promotion',
    lines: [
      `Code ${code} — ${offre} sur le panier (hors frais de livraison)`,
      minimum,
      commercantName ? `Établissement : ${commercantName}` : null,
      `Ciblage : ${ciblage}`,
      `Canaux : ${canauxLabel}`,
      `Validité : ${dates} (jusqu'à la fin du jour sélectionné)`,
      limite,
    ].filter(Boolean),
  };
}

export function getSegmentHelpText(segment, fideliteConfig) {
  if (!fideliteConfig?.actif) {
    return 'Programme fidélité inactif : seuls les clients « Tous » seront réellement segmentés. Activez la fidélité pour VIP / Fidèles / Nouveaux.';
  }
  const seuilFidele = fideliteConfig.seuilFidele ?? '—';
  const seuilVIP = fideliteConfig.seuilVIP ?? '—';
  const joursNouveau = fideliteConfig.joursNouveau ?? 30;
  switch (segment) {
    case 'NOUVEAUX':
      return `Clients inscrits depuis moins de ${joursNouveau} jours.`;
    case 'FIDELES':
      return `Clients avec au moins ${seuilFidele} commande(s) chez vous (hors annulées).`;
    case 'VIP':
      return `Clients ayant dépensé au moins ${seuilVIP} € chez vous.`;
    default:
      return 'Ouvert à tous vos clients.';
  }
}

export function needsFideliteWarning(form, fideliteConfig) {
  return (
    form.targetingMode === TARGETING_MODES.SEGMENT &&
    form.segmentClient !== 'TOUS' &&
    (!fideliteConfig || !fideliteConfig.actif)
  );
}

export function getTargetingLabel(promotion) {
  const mode = resolveTargetingMode(promotion);
  if (mode === TARGETING_MODES.CLIENTS) {
    const n = promotion.listeClientsAutorises?.length || 0;
    return `${n} client(s) ciblé(s)`;
  }
  if (mode === TARGETING_MODES.SEGMENT) {
    const seg = SEGMENT_OPTIONS.find((s) => s.value === promotion.segmentClient);
    return seg?.label || promotion.segmentClient;
  }
  return 'Tous les clients';
}

export function getChannelsLabel(promotion) {
  const canaux = Array.isArray(promotion?.canauxValides) ? promotion.canauxValides : [];
  if (canaux.length === 0) return 'Tous les canaux';
  return canaux
    .map((c) => PROMO_CHANNEL_OPTIONS.find((o) => o.value === c)?.label || c)
    .join(', ');
}
